import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Beaker, 
  Settings, 
  Download, 
  BookOpen, 
  Activity, 
  Search,
  RotateCcw,
  Camera,
  Info,
  Menu,
  X,
  Sun,
  Moon,
  Share2,
  Save,
  Sparkles
} from 'lucide-react';
import { useUIStore, useMoleculeStore, useVisualizationStore } from './store';
import { MoleculeViewer } from './components/viewer/MoleculeViewer';
import { MoleculeInput } from './components/input/MoleculeInput';
import { PropertyDashboard } from './components/dashboard/PropertyDashboard';
import { SavePanel, DownloadPanel, SharePanel } from './components/export/ExportPanels';
import { LoadingOverlay } from './components/ui/LoadingOverlay';
import { Toast } from './components/ui/Toast';
import { RenderMode } from './types';

const SIDEBAR_TABS = [
  { id: 'input', label: 'Input' },
  { id: 'properties', label: 'Properties' },
  { id: 'library', label: 'Library' },
  { id: 'settings', label: 'Settings' },
] as const;

// Element colors for legend
const ELEMENT_COLORS: Record<string, string> = {
  H: '#ffffff',
  C: '#404040',
  N: '#3050f8',
  O: '#ff0d0d',
  S: '#ffff30',
};

function App() {
  const [inputValue, setInputValue] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<'input' | 'properties' | 'library' | 'settings'>('input');
  const [showSavePanel, setShowSavePanel] = useState(false);
  const [showDownloadPanel, setShowDownloadPanel] = useState(false);
  const [showSharePanel, setShowSharePanel] = useState(false);
  
  const { 
    loading, 
    loadingMessage,
    toast,
    hideToast
  } = useUIStore();
  
  const { currentMolecule } = useMoleculeStore();
  const { settings, toggleHydrogens, toggleAtomLabels, setRenderMode } = useVisualizationStore();

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile sidebar when molecule is selected
  useEffect(() => {
    if (currentMolecule && isMobile) {
      setSidebarOpen(false);
    }
  }, [currentMolecule, isMobile]);

  const handleResetView = useCallback(() => {
    console.log('Reset view');
  }, []);

  const handleZoomIn = useCallback(() => {
    console.log('Zoom in');
  }, []);

  const handleZoomOut = useCallback(() => {
    console.log('Zoom out');
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(!sidebarOpen);
  }, [sidebarOpen]);

  const toggleTheme = useCallback(() => {
    setIsDark(!isDark);
    document.documentElement.setAttribute('data-theme', isDark ? 'light' : 'dark');
  }, [isDark]);

  // Calculate main content margin based on sidebar state
  const isPanelOpen = showSavePanel || showDownloadPanel || showSharePanel;

  return (
    <div className={`flex h-screen overflow-hidden ${isDark ? 'bg-gradient-to-br from-slate-900 to-slate-800' : 'bg-gradient-to-br from-slate-50 to-white'}`}>
      
      {/* Mobile Hamburger */}
      <button
        className={`md:hidden fixed top-4 left-4 z-50 p-3 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg ${isDark ? 'bg-slate-800 text-white' : 'bg-white text-slate-900'} shadow-lg transition-all active:scale-95`}
        onClick={toggleSidebar}
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Backdrop Overlay */}
      <AnimatePresence>
        {isMobile && sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Fixed on mobile, relative on desktop */}
      <aside 
        className={`
          ${isMobile ? 'fixed' : 'relative'}
          ${isMobile ? 'w-full max-w-sm' : 'w-80'}
          h-full
          ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}
          z-50
          transform transition-transform duration-300 ease-out
          ${isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'}
          flex flex-col
          border-r
        `}
      >
        {/* Header */}
        <div className={`p-4 border-b ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
              <Beaker className="w-6 h-6 text-white" />
            </div>
            <AnimatePresence>
              {sidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="overflow-hidden"
                >
                  <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Molecule Studio</h1>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>AI Molecular Visualizer</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Search Section - Only show when sidebar open and on Input tab */}
        <AnimatePresence>
          {sidebarOpen && activeTab === 'input' && !isPanelOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`p-4 border-b ${isDark ? 'border-slate-800' : 'border-slate-200'}`}
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Enter molecule name or SMILES..."
                className={`w-full px-4 py-3 rounded-lg border min-h-[44px] ${isDark ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-400' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
              />
              <button 
                className={`w-full mt-3 py-3 min-h-[44px] ${isDark ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-600 hover:bg-blue-500'} text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-blue-500/25 flex items-center justify-center gap-2`}
                onClick={() => console.log('Generate')}
              >
                <Sparkles className="w-5 h-5" />
                Generate Structure
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className={`${isDark ? 'border-slate-800' : 'border-slate-200'} border-b`}>
          <div className="flex" style={{ padding: sidebarOpen ? '0' : '0.5rem' }}>
            {SIDEBAR_TABS.map((tab) => (
              <button
                key={tab.id}
                className={`
                  flex-1 py-3 text-sm font-medium transition-all relative
                  ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}
                  ${activeTab === tab.id ? (isDark ? 'text-blue-400' : 'text-blue-600') : ''}
                `}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeTab"
                    className={`absolute bottom-0 left-0 right-0 h-0.5 ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable Content - Add padding bottom for sticky buttons */}
        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin pb-24">
          <AnimatePresence mode="wait">
            {sidebarOpen ? (
              <motion.div
                key={`${activeTab}-${isPanelOpen ? 'panel' : 'content'}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.15 }}
              >
                {/* Input Tab Content */}
                {activeTab === 'input' && !isPanelOpen && (
                  <MoleculeInput 
                    value={inputValue} 
                    onChange={setInputValue} 
                    showExamples={true}
                  />
                )}

                {/* Save Panel */}
                {isPanelOpen && showSavePanel && (
                  <SavePanel onClose={() => setShowSavePanel(false)} />
                )}

                {/* Download Panel */}
                {isPanelOpen && showDownloadPanel && (
                  <DownloadPanel onClose={() => setShowDownloadPanel(false)} />
                )}

                {/* Share Panel */}
                {isPanelOpen && showSharePanel && (
                  <SharePanel onClose={() => setShowSharePanel(false)} />
                )}

                {/* Properties Tab Content */}
                {activeTab === 'properties' && !isPanelOpen && (
                  <>
                    {currentMolecule ? (
                      <PropertyDashboard molecule={currentMolecule} />
                    ) : (
                      <div className="text-center py-12">
                        <Info className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          No molecule loaded. Enter a SMILES string or select an example to view properties.
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* Library Tab Content */}
                {activeTab === 'library' && !isPanelOpen && (
                  <div className="text-center py-12">
                    <BookOpen className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Your saved molecules will appear here.
                    </p>
                  </div>
                )}

                {/* Settings Tab Content */}
                {activeTab === 'settings' && !isPanelOpen && (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                      <h4 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Render Mode</h4>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { id: RenderMode.BALL_AND_STICK, label: 'Ball & Stick' },
                          { id: RenderMode.STICK, label: 'Stick' },
                          { id: RenderMode.SPACE_FILLING, label: 'Space Fill' },
                        ].map((mode) => (
                          <button
                            key={mode.id}
                            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all min-h-[44px] ${
                              settings.renderMode === mode.id
                                ? (isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white')
                                : (isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-200 text-slate-600 hover:bg-slate-300')
                            }`}
                            onClick={() => setRenderMode(mode.id)}
                          >
                            {mode.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                      <h4 className={`font-medium mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>Display Options</h4>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
                          <input
                            type="checkbox"
                            checked={settings.showHydrogens}
                            onChange={() => toggleHydrogens()}
                            className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Show Hydrogens</span>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
                          <input
                            type="checkbox"
                            checked={settings.showAtomLabels}
                            onChange={() => toggleAtomLabels()}
                            className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Show Atom Labels</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ) : (
              /* Collapsed sidebar icons */
              <div className="flex flex-col items-center gap-4 py-4">
                {SIDEBAR_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    className={`p-3 rounded-lg transition-all min-w-[44px] min-h-[44px] ${
                      activeTab === tab.id
                        ? (isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white')
                        : (isDark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-500 hover:bg-slate-100')
                    }`}
                    onClick={() => setActiveTab(tab.id as typeof activeTab)}
                    title={tab.label}
                  >
                    {tab.id === 'input' && <Search className="w-5 h-5" />}
                    {tab.id === 'properties' && <Activity className="w-5 h-5" />}
                    {tab.id === 'library' && <BookOpen className="w-5 h-5" />}
                    {tab.id === 'settings' && <Settings className="w-5 h-5" />}
                  </button>
                ))}
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Bottom Actions - Sticky with proper shadow */}
        <div className={`
          absolute bottom-0 left-0 right-0 p-4 
          ${isDark ? 'bg-slate-900/95 backdrop-blur-md border-slate-800' : 'bg-white/95 backdrop-blur-md border-slate-200'}
          border-t
          shadow-[0_-10px_30px_rgba(0,0,0,0.3)]
        `}>
          <div className="flex gap-2">
            {sidebarOpen ? (
              <>
                <button 
                  className={`flex-1 py-3 min-h-[44px] ${isDark ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-emerald-600 hover:bg-emerald-500'} text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 active:scale-95`}
                  onClick={() => {
                    setShowSavePanel(true);
                    setShowDownloadPanel(false);
                    setShowSharePanel(false);
                  }}
                >
                  <Save className="w-5 h-5" />
                  <span>Save</span>
                </button>
                <button 
                  className={`flex-1 py-3 min-h-[44px] ${isDark ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-600 hover:bg-blue-500'} text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 active:scale-95`}
                  onClick={() => {
                    setShowDownloadPanel(true);
                    setShowSavePanel(false);
                    setShowSharePanel(false);
                  }}
                >
                  <Download className="w-5 h-5" />
                  <span>Export</span>
                </button>
                <button 
                  className={`flex-1 py-3 min-h-[44px] ${isDark ? 'bg-purple-600 hover:bg-purple-500' : 'bg-purple-600 hover:bg-purple-500'} text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 active:scale-95`}
                  onClick={() => {
                    setShowSharePanel(true);
                    setShowSavePanel(false);
                    setShowDownloadPanel(false);
                  }}
                >
                  <Share2 className="w-5 h-5" />
                  <span>Share</span>
                </button>
              </>
            ) : (
              <button 
                className={`w-full p-3 min-h-[44px] flex items-center justify-center rounded-lg transition-all ${isDark ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                onClick={toggleSidebar}
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main Canvas Area */}
      <main className={`flex-1 relative overflow-hidden transition-all duration-300 ${isMobile ? '' : (sidebarOpen ? 'md:ml-80' : 'md:ml-20')}`}>
        
        {/* Top Bar */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
          <div className={`${isDark ? 'bg-white/10 backdrop-blur-md' : 'bg-white/80 backdrop-blur-md'} px-4 py-2.5 rounded-lg`}>
            <h2 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {currentMolecule?.name || 'Untitled Molecule'}
            </h2>
            <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-500'}`}>
              {currentMolecule?.formula || 'No molecule loaded'}
            </p>
          </div>
          
          <div className="flex gap-2">
            <button 
              className={`p-3 min-h-[44px] min-w-[44px] flex items-center justify-center ${isDark ? 'bg-white/10 backdrop-blur-md hover:bg-white/20' : 'bg-white/80 backdrop-blur-md hover:bg-white'} rounded-lg transition-all active:scale-95`}
              onClick={toggleTheme}
            >
              {isDark ? <Sun className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-900'}`} /> : <Moon className="w-5 h-5 text-slate-900" />}
            </button>
            <button 
              className={`p-3 min-h-[44px] min-w-[44px] flex items-center justify-center ${isDark ? 'bg-white/10 backdrop-blur-md hover:bg-white/20' : 'bg-white/80 backdrop-blur-md hover:bg-white'} rounded-lg transition-all active:scale-95`}
            >
              <Camera className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-900'}`} />
            </button>
          </div>
        </div>
        
        {/* Canvas - Full Area - NO PHONE MOCKUP */}
        <div 
          className={`absolute inset-0 w-full h-full ${isDark ? 'bg-gradient-to-br from-slate-950 to-slate-900' : 'bg-gradient-to-br from-slate-100 to-white'}`}
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <MoleculeViewer molecule={currentMolecule} />
        </div>
        
        {/* View Controls - Bottom Center */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className={`${isDark ? 'bg-slate-800/90 backdrop-blur-md' : 'bg-white/90 backdrop-blur-md'} rounded-xl p-1.5 flex gap-1 shadow-xl border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            {[
              { id: RenderMode.BALL_AND_STICK, label: 'Ball & Stick' },
              { id: RenderMode.STICK, label: 'Stick' },
              { id: RenderMode.SPACE_FILLING, label: 'Space Fill' },
            ].map((mode) => (
              <button
                key={mode.id}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all min-h-[44px] min-w-[100px] ${
                  settings.renderMode === mode.id
                    ? (isDark ? 'bg-blue-600 text-white shadow-lg' : 'bg-blue-600 text-white shadow-lg')
                    : (isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100')
                }`}
                onClick={() => setRenderMode(mode.id)}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Element Legend - Responsive and larger touch targets */}
        <div className={`
          absolute top-20 right-4 z-10
          ${isDark ? 'bg-slate-800/90 backdrop-blur-md' : 'bg-white/90 backdrop-blur-md'}
          p-2 md:p-3 rounded-xl shadow-xl border ${isDark ? 'border-slate-700' : 'border-slate-200'}
        `}>
          <div className="flex gap-2 md:gap-3">
            {Object.entries(ELEMENT_COLORS).map(([el, color]) => (
              <div key={el} className="flex flex-col items-center gap-1">
                <div 
                  className="w-6 h-6 md:w-5 md:h-5 rounded-full shadow-lg cursor-pointer active:scale-90 transition-transform"
                  style={{ backgroundColor: color, border: isDark && el === 'H' ? '1px solid #666' : 'none' }}
                />
                <span className={`text-xs md:text-[10px] font-mono font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{el}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Bottom Toolbar */}
        {isMobile && (
          <div className="fixed bottom-0 left-0 right-0 p-4 pb-6 bg-gradient-to-t from-black/60 to-transparent z-20">
            <div className={`${isDark ? 'bg-slate-800/95 backdrop-blur-md' : 'bg-white/95 backdrop-blur-md'} rounded-2xl p-2 flex justify-around items-center shadow-2xl border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <button 
                className={`p-4 rounded-xl transition-all min-w-[48px] min-h-[48px] flex items-center justify-center ${isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'} active:scale-95`}
                onClick={handleResetView}
                title="Reset View"
              >
                <RotateCcw className="w-6 h-6" />
              </button>
              <button 
                className={`p-4 rounded-xl transition-all min-w-[48px] min-h-[48px] flex items-center justify-center ${isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'} active:scale-95`}
                onClick={handleZoomIn}
                title="Zoom In"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
                </svg>
              </button>
              <button 
                className={`p-4 rounded-xl transition-all min-w-[48px] min-h-[48px] flex items-center justify-center ${isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'} active:scale-95`}
                onClick={handleZoomOut}
                title="Zoom Out"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35M8 11h6" />
                </svg>
              </button>
              <button 
                className={`p-4 rounded-xl transition-all min-w-[48px] min-h-[48px] flex items-center justify-center ${settings.showAtomLabels ? 'text-blue-400 bg-blue-500/20' : (isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100')} active:scale-95`}
                onClick={toggleAtomLabels}
                title="Toggle Labels"
              >
                <span className="text-xl font-bold">Aa</span>
              </button>
              <button 
                className={`p-4 rounded-xl transition-all min-w-[48px] min-h-[48px] flex items-center justify-center ${settings.showHydrogens ? 'text-blue-400 bg-blue-500/20' : (isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100')} active:scale-95`}
                onClick={toggleHydrogens}
                title="Toggle Hydrogens"
              >
                <span className="text-lg font-bold">H</span>
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Loading Overlay */}
      <AnimatePresence>
        {loading && (
          <LoadingOverlay message={loadingMessage} />
        )}
      </AnimatePresence>

      {/* Toast */}
      <Toast
        message={toast?.message || ''}
        type={toast?.type || 'info'}
        title={toast?.title}
        onClose={hideToast}
        visible={!!toast}
      />
    </div>
  );
}

export default App;

